import { api } from './api';

/**
 * Factory function to create CRUD services for any entity
 * This reduces duplicate code across all service files
 *
 * @param {string} baseEndpoint - The base API endpoint (e.g., 'vaccines')
 * @param {Object} options - Additional options
 * @param {string} options.listEndpoint - Custom endpoint for listing (if different from base)
 * @param {boolean} options.noSlash - If true, don't add leading slash
 * @returns {Object} CRUD service object
 */
export const createCRUDService = (baseEndpoint, options = {}) => {
  const { listEndpoint, noSlash = false } = options;
  const endpoint = noSlash ? baseEndpoint : `/${baseEndpoint}`.replace(/^\/+/, '/');
  const listUrl = listEndpoint ? `/${listEndpoint}`.replace(/^\/+/, '/') : endpoint;

  return {
    /**
     * Get all items with optional filtering and pagination
     */
    getAll: async ({ page = 1, limit = 5, status = '', search = '', ...rest } = {}) => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (status) params.append('status', status);
      if (search) params.append('search', search);

      // Add any additional parameters
      Object.entries(rest).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const queryString = params.toString();
      const url = queryString ? `${listUrl}?${queryString}` : listUrl;

      const response = await api.get(url);
      return response.data;
    },

    /**
     * Get single item by ID
     */
    getById: async (id) => {
      const response = await api.get(`${endpoint}/${id}`);
      return response.data;
    },

    /**
     * Create new item
     */
    create: async (data) => {
      const response = await api.post(endpoint, data);
      return response.data;
    },

    /**
     * Update existing item
     */
    update: async (id, data) => {
      const response = await api.put(`${endpoint}/${id}`, data);
      return response.data;
    },

    /**
     * Partial update (PATCH)
     */
    patch: async (id, data) => {
      const response = await api.patch(`${endpoint}/${id}`, data);
      return response.data;
    },

    /**
     * Delete item
     */
    delete: async (id) => {
      const response = await api.delete(`${endpoint}/${id}`);
      return response.data;
    },

    /**
     * Upload file
     */
    upload: async (file, uploadEndpoint = 'upload') => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.upload(`/${uploadEndpoint}`, formData);
      return response.data;
    },
  };
};

/**
 * Fetch dropdown data
 */
export const fetchDropdowns = async (endpoint = 'dropdowns') => {
  const response = await api.get(`/${endpoint}`);
  return response.data;
};

export default createCRUDService;
